import {
  addDays, addMonths, addYears, format, getDate, getDay, getDaysInMonth, getMonth,
  getYear, monthsInYear, startOfMonth, startOfWeek, subMonths, subYears
} from 'date-fns'
import { enUS } from 'date-fns/locale'
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { Calendar as CalendarIcon } from '../icons/Calendar'
import { Chevron } from '../icons/Chevron'
import { RADOM_COLORS } from '../util/Constants'
import { unitFn } from '../util/Util'
import { PrimaryButton } from './Button'
import { TextInput } from './Input'

const Calendar = styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px;
  cursor: default;
  max-width: 110%;
  box-sizing: border-box;
`

const CalendarHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`

const DayGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  width: 100%;
`

const MonthGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  width: 100%;
`

const YearGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 4px;
  height: 200px;
  width: 100%;
  overflow: auto;
`

const Arrow = styled(Chevron)`
  width: 12px;
  height: 12px;
  padding: 10px;
  cursor: pointer;
  border-radius: 5px;
  transition: 0.2s ease all;
  fill: ${RADOM_COLORS.GRAY_DARKER};
`

const GridItem = styled.div`
  width: 30px;
  height: 30px;
  line-height: 30px;
  justify-self: center;
`

const Day = styled(GridItem) <{ disabled: boolean, selected: boolean }>`
  text-align: center;
  border-radius: 100%;
  transition: 0.2s ease all;

  :hover {
    background-color: ${({ selected, disabled }) => selected ? RADOM_COLORS.NEW_BLUE : disabled ? 'transparent' : RADOM_COLORS.GRAY_MED}
  }

  ${({ selected }) => selected && `background-color: ${RADOM_COLORS.NEW_BLUE}; color: white;`}
  ${({ disabled }) => !disabled && 'cursor: pointer;'}
  ${({ disabled }) => disabled && 'opacity: 40%;'}
`

const Month = styled(GridItem) <{ disabled: boolean, selected: boolean }>`
  height: 40px;
  line-height: 40px;
  text-align: center;
  cursor: pointer;
  transition: 0.2s ease all;
  border-radius: 20px;
  width: 100%;
  padding: 0px 5px;

  :hover {
    background-color: ${({ selected, disabled }) => selected ? RADOM_COLORS.NEW_BLUE : disabled ? 'transparent' : RADOM_COLORS.GRAY_MED}
  }

  ${({ selected }) => selected && `background-color: ${RADOM_COLORS.NEW_BLUE}; color: white;`}
  ${({ disabled }) => !disabled && 'cursor: pointer;'}
  ${({ disabled }) => disabled && 'opacity: 40%;'}
`

const Year = styled(GridItem) <{ selected: boolean }>`
  height: 40px;
  width: 100%;
  line-height: 40px;
  text-align: center;
  cursor: pointer;
  transition: 0.2s ease all;
  border-radius: 20px;

  ${({ selected }) => !selected && `:hover { background-color: ${RADOM_COLORS.GRAY_MED}; }`}
  ${({ selected }) => selected && `background-color: ${RADOM_COLORS.NEW_BLUE};`}
  ${({ selected }) => selected && 'color: white;'}
`

// ---------------------------------------------------------------------------------------------------------------------
const DEFAULT_DATE_EARLIEST: Date = new Date(1900, 1, 1)
const DEFAULT_DATE_LATEST: Date = new Date(2100, 12, 31)

interface IProps {
  startDate: Date | undefined
  endDate: Date | undefined
  value: string | Date | undefined
  onChange: (date: Date | undefined) => void
  placeholder?: string
  innerStyle?: React.CSSProperties
}

// ---------------------------------------------------------------------------------------------------------------------
// Create a calendar date picker that operates between the start and end dates (inclusive)
// ---------------------------------------------------------------------------------------------------------------------
const DateSelector = ({
  startDate = DEFAULT_DATE_EARLIEST,
  endDate = DEFAULT_DATE_LATEST,
  value = '',
  onChange = unitFn,
  placeholder = 'Select a date',
  innerStyle = {}
}: IProps): React.ReactElement => {
  const props = {
    startDate,
    endDate,
    value,
    onChange,
    placeholder,
    innerStyle
  }

  const yearGridRef = useRef<HTMLDivElement>(null)
  const [view, setView] = useState<'day' | 'month' | 'year'>('day')
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(props.value ? new Date(props.value) : undefined)
  const [displayDate, setDisplayDate] = useState<string>(props.value ? format(new Date(props.value), 'dd/MM/yyyy') : '')
  const [dateProbe, setDateProbe] = useState<Date>(props.value ? new Date(props.value) : props.endDate)
  const [open, setOpen] = useState(false)
  const [invalid, setInvalid] = useState(false)
  const firstDOW = startOfWeek(new Date())
  const shortWeekDaysArray = Array.from(Array(7)).map((e, i) => format(addDays(firstDOW, i), 'eee'))
  const isMobile = /Mobi|Android/i.test(navigator.userAgent) || window.innerWidth < 480

  // First day of month and number of days in month of curDateProbe
  const probeDay = useMemo(() => getDay(startOfMonth(dateProbe)), [dateProbe])
  const probeDaysInMonth = useMemo(() => getDaysInMonth(dateProbe), [dateProbe])
  const containerRef = useRef<HTMLDivElement>(null)
  const [calendarPosition, setCalendarPosition] = useState({ top: 0, left: 0 })
  const calendarRef = useRef<HTMLDivElement>(null)
  const [inputWidth, setInputWidth] = useState(0)

  useLayoutEffect(() => {
    if (!open || !containerRef.current || !calendarRef.current) return

    const wrapper = containerRef.current.getBoundingClientRect()
    const calendar = calendarRef.current.getBoundingClientRect()

    setInputWidth(wrapper.width) // <--- 关键

    const bottomAlign = wrapper.bottom + 5
    const topAlign = wrapper.top - calendar.height - 5

    let top = bottomAlign
    if (bottomAlign + calendar.height > window.innerHeight && topAlign >= 0) {
      top = topAlign
    } else if (bottomAlign + calendar.height > window.innerHeight && topAlign < 0) {
      top = window.innerHeight / 2 - calendar.height / 2
    }

    let left = wrapper.left
    if (left + calendar.width > window.innerWidth) {
      left = wrapper.right - calendar.width
    }

    setCalendarPosition({ top, left })
  }, [open, view, dateProbe])

  // -------------------------------------------------------------------------------------------------------------------
  // Year view
  // -------------------------------------------------------------------------------------------------------------------
  useEffect(() => {
    if (view === 'year') {
      const row = props.startDate.getFullYear() / 3
      yearGridRef.current?.scrollTo({ top: (row * 36) - 72 })
    }
  }, [view, yearGridRef])

  const onPrevYear = (): void => {
    const prevYear = subYears(dateProbe, 1)
    if (prevYear.getFullYear() < props.startDate.getFullYear()) return

    setDateProbe(prevYear)
  }

  const onNextYear = (): void => {
    const nextYear = addYears(dateProbe, 1)
    if (props.endDate.getFullYear() < nextYear.getFullYear()) return

    setDateProbe(nextYear)
  }

  useEffect(() => {
    if (view === 'day') {
      renderDayView()
    }
    if (view === 'month') {
      renderMonthView()
    }
    if (view === 'year') {
      renderYearView()
    }
  }, [view, dateProbe])

  useEffect(() => {
    if (!open) return

    const handleClickOutside = (e: MouseEvent): void => {
      if (!containerRef.current?.contains(e.target as Node)) {
        setOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [open])

  const renderYearView = (): React.ReactElement => (
    <YearGrid ref={yearGridRef}>
      {
        Array.from({ length: props.endDate.getFullYear() - props.startDate.getFullYear() + 1 }, (_, yearNum) => {
          return <Year
            key={yearNum}
            selected={getYear(dateProbe) - props.startDate.getFullYear() === yearNum}
            onClick={() => {
              const newDate = new Date(
                props.startDate.getFullYear() + yearNum,
                dateProbe.getMonth(),
                dateProbe.getDate()
              )
              setDateProbe(newDate)
              setView('day')
            }}
          >
            {props.startDate.getFullYear() + yearNum}
          </Year>
        })
      }
    </YearGrid>
  )

  // -------------------------------------------------------------------------------------------------------------------
  // Month view
  // -------------------------------------------------------------------------------------------------------------------
  const monthIsDisabled = (monthNum: number): boolean => {
    const displayMonth = new Date(dateProbe.getFullYear(), monthNum, dateProbe.getDate())
    return displayMonth < props.startDate || displayMonth > props.endDate
  }

  const onPrevMonth = (): void => {
    const oneMonthAgo = subMonths(dateProbe, 1)
    if (
      oneMonthAgo.getFullYear() <= props.startDate.getFullYear() &&
      oneMonthAgo.getMonth() < props.startDate.getMonth()
    ) return

    setDateProbe(oneMonthAgo)
  }

  const onNextMonth = (): void => {
    const nextMonth = addMonths(dateProbe, 1)
    if (
      props.endDate.getFullYear() <= nextMonth.getFullYear() &&
      props.endDate.getMonth() < nextMonth.getMonth()
    ) return

    setDateProbe(nextMonth)
  }

  const renderMonthView = (): React.ReactElement => (
    <>
      <CalendarHeader>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>

          <PrimaryButton
            onClick={() => { setView('year') }}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
              background: 'transparent',
              color: 'black',
              gap: 10,
              fontWeight: 500,
              fontSize: 14
            }}>
            <span>{format(dateProbe, 'yyyy')}</span>
            <Chevron style={{ transform: 'rotate(0deg)' }} />
          </PrimaryButton>
          <Arrow
            onClick={onPrevYear}
            style={{ transform: 'rotate(90deg)' }}
          />
          <Arrow
            onClick={onNextYear}
            style={{ transform: 'rotate(270deg)' }}
          />
        </div>
      </CalendarHeader>

      <MonthGrid>
        {
          Array.from({ length: monthsInYear }, (_, i) => {
            const disabled = monthIsDisabled(i)
            return <Month
              key={i}
              selected={getMonth(dateProbe) === i}
              disabled={disabled}
              onClick={
                disabled
                  ? unitFn
                  : () => {
                    const selectedDate = new Date(dateProbe.getFullYear(), i, dateProbe.getDate())
                    setDateProbe(selectedDate)
                    setView('day')
                  }}
            >
              {enUS.localize?.month(i)}
            </Month>
          })
        }
      </MonthGrid>
    </>
  )

  // -------------------------------------------------------------------------------------------------------------------
  // Day view
  // -------------------------------------------------------------------------------------------------------------------
  const dayIsDisabled = (date: number): boolean => {
    const day = new Date(dateProbe.getFullYear(), dateProbe.getMonth(), date + 1)
    return day < props.startDate || day > props.endDate
  }

  const renderDayView = (): React.ReactElement => (
    <>
      <CalendarHeader>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>

          <PrimaryButton
            onClick={() => { setView('year') }}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
              background: 'transparent',
              color: 'black',
              gap: 10,
              fontWeight: 500,
              fontSize: 14
            }}>
            <span>{format(dateProbe, 'MMMM')} {format(dateProbe, 'yyyy')}</span>
            <Chevron style={{ transform: 'rotate(0deg)' }} />
          </PrimaryButton>
          <div>
            <Arrow onClick={onPrevMonth} style={{ transform: 'rotate(90deg)' }} />
            <Arrow onClick={onNextMonth} style={{ transform: 'rotate(270deg)' }} />
          </div>
        </div>
      </CalendarHeader>

      <DayGrid>
        {shortWeekDaysArray.map(d =>
          <div
            key={d}
            style={{
              textAlign: 'center',
              width: '30px',
              height: '30px',
              opacity: 0.3,
              justifySelf: 'center',
              fontWeight: 600,
              fontSize: 12
            }}>{d}</div>
        )}
        {Array.from({ length: probeDay }, (_, i) => <GridItem key={i} />)}
        {Array.from({ length: probeDaysInMonth }, (_, dayNum) => {
          const disabled = dayIsDisabled(dayNum)
          return <Day
            key={dayNum}
            disabled={disabled}
            selected={selectedDate ? getDate(selectedDate) === (dayNum + 1) : false}
            onClick={
              disabled
                ? unitFn
                : () => {
                  const selected = new Date(
                    dateProbe.getFullYear(),
                    dateProbe.getMonth(),
                    dayNum + 1,
                    12
                  )
                  setSelectedDate(selected)
                  setDisplayDate(format(selected, 'dd/MM/yyyy'))
                  props.onChange(selected)
                  setOpen(false)
                }
            }
          >
            {dayNum + 1}
          </Day>
        })
        }
      </DayGrid>
    </>
  )

  const formatDateString = (raw: string): string => {
    const parts: string[] = []
    parts.push(raw.slice(0, 2))
    if (raw.length >= 3 || raw.length > 0) parts.push(raw.slice(2, 4))
    if (raw.length >= 5 || raw.length > 2) parts.push(raw.slice(4, 8))

    const day = (parts[0] || '').padEnd(2, 'D')
    const month = (parts[1] || '').padEnd(2, 'M')
    const year = (parts[2] || '').padEnd(4, 'Y')
    return `${day}/${month}/${year}`
  }

  const mapFormattedToRawIndex = (pos: number): number => {
    if (pos <= 2) return pos
    if (pos <= 5) return pos - 1
    return pos - 2
  }

  const mapRawToFormattedIndex = (rawIdx: number): number => {
    if (rawIdx <= 2) return rawIdx
    if (rawIdx <= 4) return rawIdx + 1
    return rawIdx + 2
  }
  const onChangeDate = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const input = e.target
    const prevFormatted = displayDate
    const prevRaw = prevFormatted.replace(/\D/g, '')
    const prevCursor = input.selectionStart ?? 0
    const prevRawPos = mapFormattedToRawIndex(prevCursor)

    const newRaw = input.value.replace(/\D/g, '').slice(0, 8)
    const formatted = formatDateString(newRaw)
    setDisplayDate(formatted)

    if (newRaw.length >= 4) {
      const day = parseInt(newRaw.slice(0, 2), 10)
      const month = parseInt(newRaw.slice(2, 4), 10)
      const year = parseInt(newRaw.slice(4, 8), 10)

      if (!isNaN(month) && !isNaN(year)) {
        const tentative = new Date(year || 2000, (month || 1) - 1, day || 1)
        setDateProbe(tentative)
      }
    }

    const isDeleting = newRaw.length < prevRaw.length
    let rawCursor = prevRawPos
    if (!isDeleting) {
      rawCursor = prevRawPos + 1
    }
    rawCursor = Math.max(0, Math.min(newRaw.length, rawCursor))
    const newCursor = mapRawToFormattedIndex(rawCursor)

    setTimeout(() => {
      input.setSelectionRange(newCursor, newCursor)
    }, 0)

    if (newRaw.length === 8) {
      const day = parseInt(newRaw.slice(0, 2), 10)
      const month = parseInt(newRaw.slice(2, 4), 10)
      const year = parseInt(newRaw.slice(4, 8), 10)

      const isValid = (
        !isNaN(day) &&
        !isNaN(month) &&
        !isNaN(year) &&
        day >= 1 && day <= 31 &&
        month >= 1 && month <= 12 &&
        year >= 1900 && year <= 2100
      )

      if (isValid) {
        const date = new Date(year, month - 1, day)
        if (
          date.getFullYear() === year &&
          date.getMonth() === month - 1 &&
          date.getDate() === day
        ) {
          setSelectedDate(date)
          props.onChange(date)
          setInvalid(false)
          setDateProbe(date)
          setView('day')
        } else {
          setInvalid(true)
        }
      } else {
        setInvalid(true)
      }
    } else {
      setInvalid(false)
    }
  }

  // -------------------------------------------------------------------------------------------------------------------
  // Render calendar view using the normal (non-American) date format
  // -------------------------------------------------------------------------------------------------------------------
  return <div ref={containerRef} style={{ position: 'relative' }}>
    <CalendarIcon style={{ height: 18, position: 'absolute', right: 8, top: 7 }} stroke={RADOM_COLORS.GRAY_DARKER} onClick={
      () => {
        setOpen(!open)
        if (!open) {
          setView('day')
          setDateProbe(() => {
            const currentDate = selectedDate ?? new Date()

            // 确保日期不会超出范围
            if (currentDate > props.endDate) {
              return props.endDate
            }
            if (currentDate < props.startDate) {
              return props.startDate
            }

            return currentDate
          })
        }
      }
    } />
    <TextInput
      key={selectedDate?.toString()}
      style={{
        ...props.innerStyle,
        cursor: 'pointer',
        border: invalid ? '1px solid red' : undefined // ✅
      }}
      value={displayDate}
      placeholder={props.placeholder}
      type="text"
      onClick={() => {
        setOpen(!open)
      }}
      onChange={onChangeDate}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          const date = new Date(e.currentTarget.value)
          if (date.toString() !== 'Invalid Date') {
            setSelectedDate(date)
            props.onChange(date)
            setInvalid(false)
          } else {
            setInvalid(true)
          }
        }
      }}
    />
    {
      open && (
        <Calendar
          ref={calendarRef}
          style={{
            position: 'fixed',
            top: calendarPosition.top,
            left: calendarPosition.left,
            zIndex: 9999,
            background: 'white',
            minWidth: 250,
            borderRadius: 5,
            boxShadow: `0 0 5px ${RADOM_COLORS.GRAY_DARK}`,
            width: inputWidth,
            cursor: 'pointer'
          }}
        >
          {view === 'day' && renderDayView()}
          {view === 'month' && renderMonthView()}
          {view === 'year' && renderYearView()}
        </Calendar>
      )
    }
  </div>
}

export default DateSelector
